// Argus — DirectoryContent: tabel/listă entries cu sort + search filter + 3 view modes.
// View modes: details (table cu Name/Size/Modified), list (compact), thumbnails (grid icoane).
// Persist view mode în localStorage cheie `argus.fileExplorer.viewMode`.

import { forwardRef, useEffect, useMemo, useRef, useState, type ComponentType } from 'react';
import {
  Box,
  CircularProgress,
  TextField,
  Typography,
  InputAdornment,
} from '@mui/material';
import { VirtuosoGrid, Virtuoso, type VirtuosoHandle, type VirtuosoGridHandle } from 'react-virtuoso';
import SearchIcon from '@mui/icons-material/Search';
import { ArgusToolbar } from '../../components/floating/ArgusToolbar';
import { DrivesView } from './DrivesView';
import { EntryIcon } from './EntryIcon';
import { MiddleEllipsis } from './MiddleEllipsis';
import { ResizableHeader } from './ResizableHeader';
import { useColumnConfig, type ColumnKey } from './useColumnConfig';
import { formatEntryDisplayName, type FsEntry } from '../../lib/fsEntries';
import { formatBytes } from '../../lib/dirListing';
import { dayjs } from '../../lib/dayjsSetup';
import { useDragRectangle } from '../../lib/dragRectangle';

const VIEW_MODE_KEY = 'argus.fileExplorer.viewMode';

export type ViewMode = 'details' | 'list' | 'thumbnails' | 'largeThumbnails';
export type SortKey = 'name' | 'size' | 'modifiedAt';
export type SortDir = 'asc' | 'desc';

export function loadViewMode(): ViewMode {
  const stored = typeof window !== 'undefined' ? localStorage.getItem(VIEW_MODE_KEY) : null;
  if (stored === 'list' || stored === 'thumbnails' || stored === 'largeThumbnails' || stored === 'details') return stored;
  return 'details';
}

export function saveViewMode(mode: ViewMode): void {
  if (typeof window !== 'undefined') localStorage.setItem(VIEW_MODE_KEY, mode);
}

export interface DirectoryContentProps {
  entries: readonly FsEntry[];
  isLoading: boolean;
  isDrivesRoot?: boolean;
  agentId: string;
  parentPath: string;
  viewMode: ViewMode;
  selectedEntryName?: string | null;
  onSelect?: (entry: FsEntry | null) => void;
  onOpenDir: (name: string) => void;
  onOpenFile: (entry: FsEntry) => void;
  onContextMenu?: (entry: FsEntry, position: { top: number; left: number }) => void;
  /** Controlled sort (optional). Dacă oferit, state intern e ignorat. */
  sortKey?: SortKey;
  sortDir?: SortDir;
  onSortChange?: (key: SortKey, dir: SortDir) => void;
  /** True (default) = enable lazy load real thumbnails picture/video; false = doar iconițe. */
  realMediaThumbnails?: boolean;
  /** Bloc file-explorer-multiselect-download 2026-05-06: multi-select state lifted la parent. */
  selectedNames?: ReadonlySet<string> | undefined;
  onMultiClick?: ((entryName: string, modifiers: { ctrlKey: boolean; shiftKey: boolean }) => void) | undefined;
  onMultiSetSelection?: ((next: ReadonlySet<string>, modifiers?: { ctrlKey: boolean; shiftKey: boolean }) => void) | undefined;
  /** Pasăm entries sortate înapoi la parent pentru SHIFT range + drag rectangle ordering. */
  onOrderedEntriesChange?: (orderedNames: readonly string[]) => void;
}

export function DirectoryContent({ entries, isLoading, isDrivesRoot = false, agentId, parentPath, viewMode, selectedEntryName = null, onSelect, onOpenDir, onOpenFile, onContextMenu, sortKey: sortKeyProp, sortDir: sortDirProp, onSortChange, realMediaThumbnails = true, selectedNames, onMultiClick, onMultiSetSelection, onOrderedEntriesChange }: DirectoryContentProps): JSX.Element {
  const [search, setSearch] = useState('');
  const [sortKeyState, setSortKeyState] = useState<SortKey>('name');
  const [sortDirState, setSortDirState] = useState<SortDir>('asc');
  const sortKey = sortKeyProp ?? sortKeyState;
  const sortDir = sortDirProp ?? sortDirState;

  const sortedFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q === '' ? [...entries] : entries.filter((e) => e.name.toLowerCase().includes(q));
    filtered.sort((a, b) => {
      if (a.isDir !== b.isDir) return a.isDir ? -1 : 1;
      const dir = sortDir === 'asc' ? 1 : -1;
      if (sortKey === 'name') return dir * a.name.localeCompare(b.name, 'ro');
      if (sortKey === 'size') return dir * ((a.size ?? -1) - (b.size ?? -1));
      const aTime = a.modifiedAt ? new Date(a.modifiedAt).getTime() : 0;
      const bTime = b.modifiedAt ? new Date(b.modifiedAt).getTime() : 0;
      return dir * (aTime - bTime);
    });
    return filtered;
  }, [entries, search, sortKey, sortDir]);

  // Bloc file-explorer-multiselect-download: notifică parent ordinea curentă pentru SHIFT range.
  useEffect(() => {
    if (onOrderedEntriesChange) {
      onOrderedEntriesChange(sortedFiltered.map((e) => e.name));
    }
  }, [sortedFiltered, onOrderedEntriesChange]);

  // Bloc v1.0.10 fix UX — toggleSort: setSortKey + setSortDir are 2 setters separate, fiecare
  // emite onSortChange independent cu valori VECHI din closure. Al doilea overwrite primul cu
  // sortKey vechi → click pe Size/Modified nu schimba key. Fix: emit onSortChange UNA SINGURA dată
  // cu ambele valori noi (sau setState dual cand uncontrolled).
  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      const nextDir: SortDir = sortDir === 'asc' ? 'desc' : 'asc';
      if (onSortChange) onSortChange(key, nextDir);
      else setSortDirState(nextDir);
    } else {
      if (onSortChange) onSortChange(key, 'asc');
      else { setSortKeyState(key); setSortDirState('asc'); }
    }
  };

  // Drives root: render dedicat Windows-style fără toolbar search/view modes (drives au propria reprezentare).
  if (isDrivesRoot) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
        <DrivesView entries={entries} agentId={agentId} selectedEntryName={selectedEntryName} onSelect={onSelect} onOpenDrive={onOpenDir} />
        {isLoading ? (
          <Box
            sx={{
              position: 'absolute', inset: 0,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5,
              backgroundColor: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(2px)', zIndex: 5,
            }}
          >
            <CircularProgress size={36} />
            <Typography variant="body2">Loading directory…</Typography>
          </Box>
        ) : null}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <ArgusToolbar>
        <TextField
          size="small"
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ flexGrow: 1, '& .MuiInputBase-root': { height: 30, fontSize: 13 } }}
        />
      </ArgusToolbar>

      <Box sx={{ flexGrow: 1, position: 'relative', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {viewMode === 'details' ? (
          <DetailsView entries={sortedFiltered} agentId={agentId} parentPath={parentPath} sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} selectedEntryName={selectedEntryName} onSelect={onSelect} onOpenDir={onOpenDir} onOpenFile={onOpenFile} onContextMenu={onContextMenu} selectedNames={selectedNames} onMultiClick={onMultiClick} onMultiSetSelection={onMultiSetSelection} />
        ) : viewMode === 'list' ? (
          <ListView entries={sortedFiltered} agentId={agentId} selectedEntryName={selectedEntryName} onSelect={onSelect} onOpenDir={onOpenDir} onOpenFile={onOpenFile} onContextMenu={onContextMenu} selectedNames={selectedNames} onMultiClick={onMultiClick} onMultiSetSelection={onMultiSetSelection} />
        ) : (
          <ThumbnailsView entries={sortedFiltered} agentId={agentId} parentPath={parentPath} selectedEntryName={selectedEntryName} onSelect={onSelect} onOpenDir={onOpenDir} onOpenFile={onOpenFile} onContextMenu={onContextMenu} thumbSize={viewMode === 'largeThumbnails' ? 96 : 48} realMediaThumbnails={realMediaThumbnails} selectedNames={selectedNames} onMultiClick={onMultiClick} onMultiSetSelection={onMultiSetSelection} />
        )}

        {isLoading ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              backgroundColor: 'rgba(0,0,0,0.35)',
              backdropFilter: 'blur(2px)',
              zIndex: 5,
            }}
          >
            <CircularProgress size={36} />
            <Typography variant="body2" color="text.primary">
              Loading directory…
            </Typography>
          </Box>
        ) : sortedFiltered.length === 0 ? (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
            <Typography variant="body2">No files. Try refreshing.</Typography>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

interface ViewProps {
  entries: readonly FsEntry[];
  agentId: string;
  parentPath?: string;
  selectedEntryName?: string | null;
  onSelect?: ((entry: FsEntry | null) => void) | undefined;
  onOpenDir: (name: string) => void;
  onOpenFile: (entry: FsEntry) => void;
  onContextMenu: ((entry: FsEntry, position: { top: number; left: number }) => void) | undefined;
  // Bloc file-explorer-multiselect-download.
  selectedNames?: ReadonlySet<string> | undefined;
  onMultiClick?: ((entryName: string, modifiers: { ctrlKey: boolean; shiftKey: boolean }) => void) | undefined;
  onMultiSetSelection?: ((next: ReadonlySet<string>, modifiers?: { ctrlKey: boolean; shiftKey: boolean }) => void) | undefined;
}

function makeContextHandler(entry: FsEntry, onContextMenu?: ViewProps['onContextMenu']) {
  return (e: React.MouseEvent) => {
    if (!onContextMenu) return;
    e.preventDefault();
    onContextMenu(entry, { top: e.clientY, left: e.clientX });
  };
}

interface DetailsViewProps {
  entries: readonly FsEntry[];
  agentId: string;
  parentPath: string;
  selectedEntryName?: string | null;
  onSelect?: ((entry: FsEntry | null) => void) | undefined;
  onOpenDir: (name: string) => void;
  onOpenFile: (entry: FsEntry) => void;
  onContextMenu: ViewProps['onContextMenu'];
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
  // Bloc file-explorer-multiselect-download.
  selectedNames?: ReadonlySet<string> | undefined;
  onMultiClick?: ((entryName: string, modifiers: { ctrlKey: boolean; shiftKey: boolean }) => void) | undefined;
  onMultiSetSelection?: ((next: ReadonlySet<string>, modifiers?: { ctrlKey: boolean; shiftKey: boolean }) => void) | undefined;
}

const COLUMN_LABELS: Record<ColumnKey, string> = {
  name: 'Name',
  size: 'Size',
  modifiedAt: 'Modified',
};

function colSortKey(col: ColumnKey): SortKey | null {
  if (col === 'name') return 'name';
  if (col === 'size') return 'size';
  if (col === 'modifiedAt') return 'modifiedAt';
  return null;
}

function entryStyle(entry: FsEntry): React.CSSProperties {
  if (entry.isHidden || entry.isSystem) {
    return { opacity: 0.5, fontStyle: 'italic' };
  }
  return {};
}

const detailsCellStyle: React.CSSProperties = {
  padding: '4px 8px',
  fontSize: 13,
  borderBottom: '1px solid rgba(255,255,255,0.08)',
  borderRight: '1px solid rgba(255,255,255,0.06)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

function DetailsView({ entries, agentId, parentPath, sortKey, sortDir, onSort, selectedEntryName = null, onSelect, onOpenDir, onOpenFile, onContextMenu, selectedNames, onMultiClick, onMultiSetSelection }: DetailsViewProps): JSX.Element {
  const { widths, order, setWidth, setOrder } = useColumnConfig(agentId, parentPath);
  const [draggedCol, setDraggedCol] = useState<ColumnKey | null>(null);
  const [dragOverCol, setDragOverCol] = useState<ColumnKey | null>(null);
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const detailsBodyRef = useRef<HTMLDivElement>(null);

  // Bloc v1.0.10 fix UX — minimal scroll. Reguli:
  //  - element complet vizibil → niciun scroll (return early)
  //  - element top hidden (deasupra viewport) → align: 'start' (top la container top)
  //  - element bottom hidden (sub viewport, inclusiv nume) → align: 'end' (bottom la container bottom)
  useEffect(() => {
    if (!selectedEntryName) return;
    const idx = entries.findIndex((e) => e.name === selectedEntryName);
    if (idx < 0) return;
    const t = window.setTimeout(() => {
      const vh = virtuosoRef.current as unknown as { scrollerRef?: HTMLElement | null } | null;
      const scroller = vh && vh.scrollerRef ? vh.scrollerRef : null;
      if (scroller === null) return;
      const items = scroller.querySelectorAll('[data-index]');
      const target = Array.from(items).find((el) => (el as HTMLElement).dataset.index === String(idx)) as HTMLElement | undefined;
      if (target === undefined) {
        // Off-screen rendering Virtuoso — fallback align 'start' (cele mai frecvente cazuri navigation).
        virtuosoRef.current?.scrollToIndex({ index: idx, align: 'start', behavior: 'smooth' });
        return;
      }
      const tr = target.getBoundingClientRect();
      const sr = scroller.getBoundingClientRect();
      if (tr.top >= sr.top && tr.bottom <= sr.bottom) return; // fully visible — no scroll
      const align: 'start' | 'end' = tr.top < sr.top ? 'start' : 'end';
      virtuosoRef.current?.scrollToIndex({ index: idx, align, behavior: 'smooth' });
    }, 30);
    return () => window.clearTimeout(t);
  }, [selectedEntryName, entries]);

  const handleDrop = (target: ColumnKey) => {
    if (!draggedCol || draggedCol === target) return;
    const next = [...order];
    const fromIdx = next.indexOf(draggedCol);
    const toIdx = next.indexOf(target);
    if (fromIdx < 0 || toIdx < 0) return;
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, draggedCol);
    setOrder(next);
    setDraggedCol(null);
    setDragOverCol(null);
  };

  const renderHeaderCell = (col: ColumnKey) => {
    const sortColKey = colSortKey(col);
    const isActiveSort = sortColKey !== null && sortKey === sortColKey;
    const arrow = isActiveSort ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
    const labelNode = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, cursor: sortColKey ? 'pointer' : 'default', color: isActiveSort ? 'primary.main' : 'inherit' }}>
        <span>{COLUMN_LABELS[col]}</span>
        {arrow ? <span style={{ fontSize: 10 }}>{arrow}</span> : null}
      </Box>
    );
    return (
      <ResizableHeader
        key={col}
        width={widths[col]}
        onResize={(w) => setWidth(col, w)}
        draggable
        onDragStart={() => setDraggedCol(col)}
        onDragEnd={() => { setDraggedCol(null); setDragOverCol(null); }}
        onDragOver={() => setDragOverCol(col)}
        onDrop={() => handleDrop(col)}
        isDragOver={dragOverCol === col && draggedCol !== null && draggedCol !== col}
        onHeaderClick={sortColKey ? () => onSort(sortColKey) : undefined}
      >
        {col === 'size' ? (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>{labelNode}</Box>
        ) : labelNode}
      </ResizableHeader>
    );
  };

  const renderDataCell = (col: ColumnKey, entry: FsEntry) => {
    const rowStyle: React.CSSProperties = {
      ...detailsCellStyle,
      ...entryStyle(entry),
      width: widths[col],
      minWidth: widths[col],
      maxWidth: widths[col],
      cursor: 'pointer',
      overflow: 'hidden',
    };
    if (col === 'name') {
      return (
        <Box
          key={col}
          sx={{ ...rowStyle, display: 'flex', alignItems: 'center', gap: 1 }}
          onDoubleClick={() => entry.isDir ? onOpenDir(entry.name) : onOpenFile(entry)}
          onContextMenu={makeContextHandler(entry, onContextMenu)}
        >
          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
            <EntryIcon entry={entry} agentId={agentId} size={20} />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <MiddleEllipsis text={formatEntryDisplayName(entry)} />
          </Box>
        </Box>
      );
    }
    if (col === 'size') {
      return <Box key={col} sx={{ ...rowStyle, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>{entry.isDir ? '—' : formatBytes(entry.size)}</Box>;
    }
    return <Box key={col} sx={{ ...rowStyle, display: 'flex', alignItems: 'center' }}>{entry.modifiedAt ? dayjs(entry.modifiedAt).format('DD MMM YYYY HH:mm') : '—'}</Box>;
  };

  // Header HTML separat în container fix overflow:hidden — NU împărtășește scroll cu lista entries.
  // Lista jos = Virtuoso cu propriul scrollbar V doar pe entries.
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box
        sx={{
          flexShrink: 0,
          overflow: 'hidden',
          backgroundColor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box
          component="table"
          sx={{
            borderCollapse: 'collapse',
            tableLayout: 'fixed',
          }}
        >
          <thead>
            <tr>
              {order.map((col) => renderHeaderCell(col))}
            </tr>
          </thead>
        </Box>
      </Box>
      <Box ref={detailsBodyRef} sx={{ flexGrow: 1, minHeight: 0, position: 'relative' }}>
        <Virtuoso
          ref={virtuosoRef}
          data={entries as FsEntry[]}
          style={{ height: '100%' }}
          itemContent={(_index, entry) => {
            const isMultiSelected = selectedNames?.has(entry.name) ?? false;
            const isPrimarySelected = entry.name === selectedEntryName;
            const isHighlighted = isMultiSelected || isPrimarySelected;
            const handleClick = (e: React.MouseEvent) => {
              if (onMultiClick) {
                onMultiClick(entry.name, { ctrlKey: e.ctrlKey, shiftKey: e.shiftKey });
              }
              onSelect?.(entry);
            };
            return (
              <Box
                data-fs-entry={entry.name}
                onClick={handleClick}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'stretch',
                  cursor: 'pointer',
                  backgroundColor: isHighlighted ? 'action.selected' : undefined,
                  '&:hover': { backgroundColor: isHighlighted ? 'action.selected' : 'action.hover' },
                }}
              >
                {order.map((col) => renderDataCell(col, entry))}
              </Box>
            );
          }}
        />
        {onMultiSetSelection ? <DragRectangleOverlay containerRef={detailsBodyRef} entries={entries} onMultiSetSelection={onMultiSetSelection} selectedNames={selectedNames} /> : null}
      </Box>
    </Box>
  );
}

interface DragRectangleOverlayProps {
  containerRef: React.RefObject<HTMLElement>;
  entries: readonly FsEntry[];
  onMultiSetSelection: (next: ReadonlySet<string>, modifiers?: { ctrlKey: boolean; shiftKey: boolean }) => void;
  selectedNames?: ReadonlySet<string> | undefined;
}

function DragRectangleOverlay({ containerRef, entries: _entries, onMultiSetSelection, selectedNames }: DragRectangleOverlayProps): JSX.Element {
  // Snapshot live de selectedNames pentru getInitialSelection (capture la mousedown).
  const selectedRef = useRef<ReadonlySet<string>>(selectedNames ?? new Set());
  useEffect(() => { selectedRef.current = selectedNames ?? new Set(); }, [selectedNames]);

  const { rectStyle } = useDragRectangle<string>({
    containerRef,
    itemSelector: '[data-fs-entry]',
    getKeyFromElement: (el) => el.getAttribute('data-fs-entry'),
    ignoreSelector: '[data-fs-entry], [role=row], thead, th',
    getInitialSelection: () => selectedRef.current,
    onLiveUpdate: (merged, modifiers) => {
      // Live preview: actualizare imediată state pentru highlight items intersectate.
      onMultiSetSelection(merged, modifiers);
    },
    onComplete: (merged, modifiers) => {
      onMultiSetSelection(merged, modifiers);
    },
    onEmptyClick: () => {
      // Click simplu pe spațiu gol fără modifier → clear selection.
      onMultiSetSelection(new Set(), { ctrlKey: false, shiftKey: false });
    },
  });
  return <Box sx={rectStyle} />;
}

function ListView({ entries, agentId, selectedEntryName = null, onSelect, onOpenDir, onOpenFile, onContextMenu, selectedNames, onMultiClick, onMultiSetSelection }: ViewProps): JSX.Element {
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const listBodyRef = useRef<HTMLDivElement>(null);

  // Bloc v1.0.10 fix UX — minimal scroll (pattern identic DetailsView):
  //  - vizibil → no scroll; top hidden → align start; bottom hidden → align end.
  useEffect(() => {
    if (!selectedEntryName) return;
    const idx = entries.findIndex((e) => e.name === selectedEntryName);
    if (idx < 0) return;
    const t = window.setTimeout(() => {
      const vh = virtuosoRef.current as unknown as { scrollerRef?: HTMLElement | null } | null;
      const scroller = vh && vh.scrollerRef ? vh.scrollerRef : null;
      if (scroller === null) return;
      const items = scroller.querySelectorAll('[data-index]');
      const target = Array.from(items).find((el) => (el as HTMLElement).dataset.index === String(idx)) as HTMLElement | undefined;
      if (target === undefined) {
        virtuosoRef.current?.scrollToIndex({ index: idx, align: 'start', behavior: 'smooth' });
        return;
      }
      const tr = target.getBoundingClientRect();
      const sr = scroller.getBoundingClientRect();
      if (tr.top >= sr.top && tr.bottom <= sr.bottom) return;
      const align: 'start' | 'end' = tr.top < sr.top ? 'start' : 'end';
      virtuosoRef.current?.scrollToIndex({ index: idx, align, behavior: 'smooth' });
    }, 30);
    return () => window.clearTimeout(t);
  }, [selectedEntryName, entries]);

  return (
    <Box ref={listBodyRef} sx={{ height: '100%', flexGrow: 1, position: 'relative' }}>
      <Virtuoso
        ref={virtuosoRef}
        data={entries as FsEntry[]}
        style={{ height: '100%' }}
        itemContent={(_index, entry) => {
          const isMulti = selectedNames?.has(entry.name) ?? false;
          const isPrimary = entry.name === selectedEntryName;
          const isHighlighted = isMulti || isPrimary;
          const handleClick = (e: React.MouseEvent) => {
            if (onMultiClick) onMultiClick(entry.name, { ctrlKey: e.ctrlKey, shiftKey: e.shiftKey });
            onSelect?.(entry);
          };
          return (
            <Box
              data-fs-entry={entry.name}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1, p: 0.5, borderRadius: 0.5,
                cursor: 'pointer', minWidth: 0,
                backgroundColor: isHighlighted ? 'action.selected' : undefined,
                '&:hover': { backgroundColor: isHighlighted ? 'action.selected' : 'action.hover' },
                ...entryStyle(entry),
              }}
              onClick={handleClick}
              onDoubleClick={() => entry.isDir ? onOpenDir(entry.name) : onOpenFile(entry)}
              onContextMenu={makeContextHandler(entry, onContextMenu)}
            >
              <EntryIcon entry={entry} agentId={agentId} size={20} />
              <Box sx={{ flexGrow: 1, minWidth: 0, fontSize: 14 }}>
                <MiddleEllipsis text={formatEntryDisplayName(entry)} />
              </Box>
            </Box>
          );
        }}
      />
      {onMultiSetSelection ? <DragRectangleOverlay containerRef={listBodyRef} entries={entries} onMultiSetSelection={onMultiSetSelection} selectedNames={selectedNames} /> : null}
    </Box>
  );
}

const ThumbnailsGridList: ComponentType<any> = forwardRef<HTMLDivElement, any>(function ThumbnailsGridList(props, ref) {
  return (
    <div
      {...props}
      ref={ref}
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))',
        alignContent: 'flex-start',
        gap: 8,
        padding: 0,
        ...(props.style ?? {}),
      }}
    />
  );
});

function ThumbnailsView({ entries, agentId, parentPath, selectedEntryName = null, onSelect, onOpenDir, onOpenFile, onContextMenu, thumbSize = 48, realMediaThumbnails = true, selectedNames, onMultiClick, onMultiSetSelection }: ViewProps & { thumbSize?: number; realMediaThumbnails?: boolean }): JSX.Element {
  const gridRef = useRef<VirtuosoGridHandle>(null);
  const thumbsBodyRef = useRef<HTMLDivElement>(null);
  // Visible range tracking — Virtuoso rangeChanged fires cu startIndex / endIndex.
  // Buffer ±10 entries peste viewport pentru a fi proactiv la scroll rapid.
  const [visibleSet, setVisibleSet] = useState<Set<number>>(new Set());
  const debounceTimer = useRef<number | null>(null);

  // Bloc v1.0.10 fix UX — minimal scroll. Reguli (consistent cu DetailsView/ListView):
  //  - element complet vizibil → no scroll
  //  - top hidden (deasupra viewport) → align: 'start'
  //  - bottom hidden (sub viewport) → align: 'end'
  useEffect(() => {
    if (!selectedEntryName) return;
    const idx = entries.findIndex((e) => e.name === selectedEntryName);
    if (idx < 0) return;
    const t = window.setTimeout(() => {
      const el = gridRef.current as unknown as { scrollerRef?: HTMLElement | null } | null;
      const scroller = el && el.scrollerRef ? el.scrollerRef : null;
      if (scroller === null) return;
      const items = scroller.querySelectorAll('[role="gridcell"], [data-index]');
      const target = items[idx] as HTMLElement | undefined;
      if (target === undefined) {
        gridRef.current?.scrollToIndex({ index: idx, align: 'start', behavior: 'smooth' });
        return;
      }
      const tr = target.getBoundingClientRect();
      const sr = scroller.getBoundingClientRect();
      if (tr.top >= sr.top && tr.bottom <= sr.bottom) return;
      const align: 'start' | 'end' = tr.top < sr.top ? 'start' : 'end';
      gridRef.current?.scrollToIndex({ index: idx, align, behavior: 'smooth' });
    }, 30);
    return () => window.clearTimeout(t);
  }, [selectedEntryName, entries]);

  useEffect(() => () => {
    if (debounceTimer.current !== null) window.clearTimeout(debounceTimer.current);
  }, []);

  const handleRangeChanged = ({ startIndex, endIndex }: { startIndex: number; endIndex: number }) => {
    if (debounceTimer.current !== null) window.clearTimeout(debounceTimer.current);
    debounceTimer.current = window.setTimeout(() => {
      const next = new Set<number>();
      const lo = Math.max(0, startIndex - 10);
      const hi = Math.min(entries.length - 1, endIndex + 10);
      for (let i = lo; i <= hi; i++) next.add(i);
      setVisibleSet(next);
    }, 200);
  };

  return (
    <Box ref={thumbsBodyRef} sx={{ height: '100%', flexGrow: 1, position: 'relative' }}>
    <VirtuosoGrid
      ref={gridRef}
      data={entries as FsEntry[]}
      style={{ height: '100%' }}
      components={{
        List: ThumbnailsGridList,
      }}
      rangeChanged={handleRangeChanged}
      itemContent={(index, entry) => {
        const isMulti = selectedNames?.has(entry.name) ?? false;
        const isPrimary = entry.name === selectedEntryName;
        const isHighlighted = isMulti || isPrimary;
        const isVisible = visibleSet.has(index);
        const handleClick = (e: React.MouseEvent) => {
          if (onMultiClick) onMultiClick(entry.name, { ctrlKey: e.ctrlKey, shiftKey: e.shiftKey });
          onSelect?.(entry);
        };
        return (
        <Box
          data-fs-entry={entry.name}
          sx={{
            p: 1, borderRadius: 1, cursor: 'pointer', textAlign: 'center',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            backgroundColor: isHighlighted ? 'action.selected' : undefined,
            '&:hover': { backgroundColor: isHighlighted ? 'action.selected' : 'action.hover' },
            ...entryStyle(entry),
          }}
          onClick={handleClick}
          onDoubleClick={() => entry.isDir ? onOpenDir(entry.name) : onOpenFile(entry)}
          onContextMenu={makeContextHandler(entry, onContextMenu)}
        >
          <EntryIcon entry={entry} agentId={agentId} size={thumbSize} parentPath={parentPath} isVisible={isVisible} enableThumbnail={realMediaThumbnails} />
          <Typography
            variant="caption"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              wordBreak: 'break-word',
              mt: 0.5,
              minHeight: '2.4em',
              maxHeight: '2.4em',
              lineHeight: '1.2em',
            }}
            title={formatEntryDisplayName(entry)}
          >
            {formatEntryDisplayName(entry)}
          </Typography>
        </Box>
        );
      }}
    />
    {onMultiSetSelection ? <DragRectangleOverlay containerRef={thumbsBodyRef} entries={entries} onMultiSetSelection={onMultiSetSelection} selectedNames={selectedNames} /> : null}
    </Box>
  );
}
